import { useCallback, useMemo, useState } from "react";
import type { AssistantClusterDefaults } from "../api/types";
import { useSort } from "../hooks/useSort";
import { ENV_LABELS, envRank } from "../util/env";
import { ChartVersionCell, ClusterLink, FilterCell } from "./assistantCells";

// Cluster-level genfx_assistant defaults for the core fleet: one row per cluster, the
// chart ← cluster values.yaml baseline every namespace inherits. Values the cluster's
// values.yaml does not set itself render as inherited (chart default) — that is what a
// namespace gets when it overrides nothing, but nobody chose it per cluster.
type ValueCol = "imageRegistry" | "tag" | "runnerTag" | "llmAssistantModel" | "llmCodeModel" | "llmAgentGenEndpoint";

const VALUE_COLS: { col: ValueCol; label: string }[] = [
    { col: "imageRegistry", label: "ImageRegistry" },
    { col: "tag", label: "Tag" },
    { col: "runnerTag", label: "RunnerTag" },
    { col: "llmAssistantModel", label: "AssistantModel" },
    { col: "llmCodeModel", label: "CodeModel" },
    { col: "llmAgentGenEndpoint", label: "AgentGenEndpoint" },
];

function cellText(row: AssistantClusterDefaults, col: string): string {
    if (col === "env") return row.env ?? "";
    const v = row[col as keyof AssistantClusterDefaults];
    if (typeof v === "boolean") return v ? "yes" : "no";
    if (Array.isArray(v)) return v.join(",");
    return String(v ?? "");
}

export default function AssistantClustersTable({ clusters }: { clusters: AssistantClusterDefaults[] }) {
    const [colFilters, setColFilters] = useState<Record<string, string>>({});
    const [fullWidth, setFullWidth] = useState(false);
    const setFilter = (key: string, val: string) => setColFilters((f) => ({ ...f, [key]: val }));

    const filtered = useMemo(() => {
        const active = Object.entries(colFilters).filter(([, val]) => val.trim() !== "");
        return clusters.filter((r) => active.every(([key, val]) => cellText(r, key).toLowerCase().includes(val.trim().toLowerCase())));
    }, [clusters, colFilters]);

    // Default order mirrors the Core tab: env groups dev → eng → qa → prod, then name.
    const sortVal = useCallback((r: AssistantClusterDefaults, col: string): string => {
        if (col === "env") return `${envRank(r.env)}:${r.cluster}`;
        if (col === "cluster") return r.cluster;
        return cellText(r, col);
    }, []);
    const { sorted, toggle, arrow } = useSort(filtered, sortVal, "env");

    const headers: { col: string; label: string }[] = [
        { col: "cluster", label: "Cluster" },
        { col: "env", label: "Env" },
        ...VALUE_COLS,
        { col: "chartVersion", label: "ChartVer" },
    ];

    return (
        <div>
            <div className="assistant-filters">
                <label>
                    <input type="checkbox" checked={fullWidth} onChange={(e) => setFullWidth(e.target.checked)} /> full width
                </label>
                {Object.values(colFilters).some((v) => v.trim() !== "") ? (
                    <button className="btn btn-small" onClick={() => setColFilters({})}>
                        Clear filters
                    </button>
                ) : null}
                <span className="muted">
                    {filtered.length} / {clusters.length} cluster(s)
                </span>
                <span className="muted">
                    <span className="inherited">italic</span> = not set in cluster values.yaml, inherited from chart defaults
                </span>
            </div>

            {clusters.length === 0 ? (
                <p className="muted footnote">No cluster defaults loaded yet.</p>
            ) : (
                <div className={`table-wrap${fullWidth ? " full-bleed" : ""}`}>
                    <table>
                        <thead>
                            <tr>
                                {headers.map((h) => (
                                    <th key={h.col} className="sortable" onClick={() => toggle(h.col)}>
                                        {h.label} <span className="sort-arrow">{arrow(h.col)}</span>
                                    </th>
                                ))}
                            </tr>
                            <tr className="filter-row">
                                {headers.map((h) => (
                                    <FilterCell key={h.col} col={h.col} value={colFilters[h.col] ?? ""} onChange={setFilter} />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((r) => (
                                <tr key={r.cluster}>
                                    <td>
                                        <ClusterLink cluster={r.cluster} url={r.clusterValuesUrl} />
                                        {r.cloud ? <span className={`cloud-badge ${r.cloud}`}>{r.cloud.toUpperCase()}</span> : null}
                                    </td>
                                    <td>{r.env ? (ENV_LABELS[r.env] ?? r.env) : <span className="muted">—</span>}</td>
                                    {VALUE_COLS.map(({ col }) => (
                                        <ValueCell key={col} row={r} col={col} />
                                    ))}
                                    <ChartVersionCell
                                        chartVersion={r.chartVersion}
                                        defaultsSource={r.defaultsSource}
                                        url={r.chartVersionUrl}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ValueCell({ row, col }: { row: AssistantClusterDefaults; col: ValueCol }) {
    const text = row[col];
    if (!text) return <td className="muted">—</td>;
    const setHere = row.clusterKeys.includes(col);
    return (
        <td
            className={setHere ? "" : "inherited"}
            title={setHere ? "set in cluster values.yaml" : "chart default — not set in cluster values.yaml"}
        >
            {text}
        </td>
    );
}
